import {
  CARD_LIFT_MAX,
  type ColorTokens,
  DEFAULT_THEME,
  type ThemeDoc,
  ThemeDocSchema,
  cardFill,
  fieldBg,
  mix,
  paletteRoles,
  radiusScale,
  readableOn,
  safeFont,
  softenToward,
  withAlpha,
} from '@event67/shared';

// The event's theme document, turned into the CSS custom properties every
// component on this site paints with.
//
// Both palettes are emitted in one stylesheet — the light one at the root, the
// dark one inside `@media (prefers-color-scheme: dark)`. The browser picks.
// Doing it that way rather than reading `matchMedia` in React means the theme is
// correct in the first painted frame, survives the user flipping their system
// setting mid-visit with no re-render, and is a pure string this module can be
// tested on without a DOM.

/**
 * The palette a page wears before the event's theme has loaded, when the theme
 * fails to parse, and on the not-found page — where there is no event to ask.
 *
 * It is the API's own default rather than a neutral palette invented here: an
 * event that has never saved a theme is served exactly this, so a page that
 * renders before the fetch lands and a page that renders after it look the
 * same, and nothing flashes.
 */
export const FALLBACK_THEME: ThemeDoc = DEFAULT_THEME;

/**
 * The palette maths moved to `@event67/shared/theming/palette_roles`.
 *
 * It moved because the admin console's form preview has to derive its colours
 * from the same rules, and a hand-styled mock could never match the page it
 * claimed to be a preview of. Re-exported rather than re-imported at every call
 * site so nothing in this package changed shape, and so the tests that pin the
 * derivations keep pointing at this module.
 */
export {
  CARD_LIFT_MAX,
  cardFill,
  fieldBg,
  mix,
  paletteRoles,
  radiusScale,
  readableOn,
  safeFont,
  softenToward,
  withAlpha,
};

const SCALE: Record<string, number> = { compact: 0.94, comfortable: 1, spacious: 1.06 };
const DENSITY: Record<string, number> = { compact: 0.85, regular: 1, comfortable: 1.15 };

/**
 * A number from a lookup table, by a key we do not control.
 *
 * `TABLE[key] ?? fallback` is the obvious form and it is wrong here, because
 * `key` reaches this from a JSON document: `SCALE['constructor']` is not
 * undefined, it is `Object` — so `??` never fires and
 * `function Object() { [native code] }` is written into the stylesheet. The
 * same trick returns `[object Object]` through `__proto__` and a function body
 * through `toString`.
 *
 * Own properties only, and the result must actually be a finite number.
 */
const lookupNumber = (table: Record<string, number>, key: unknown, fallback: number): number => {
  if (typeof key !== 'string' || !Object.hasOwn(table, key)) return fallback;
  const value = table[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

/**
 * The event's palette as this site's CSS custom properties.
 *
 * A naming layer over `paletteRoles` and nothing more — every value is decided
 * there, so the console's preview and this page cannot disagree about a colour.
 */
export const paletteVars = (tokens: ColorTokens): Record<string, string> => {
  const r = paletteRoles(tokens);
  return {
    '--e67-primary': r.primary,
    '--e67-on-primary': r.onPrimary,
    '--e67-primary-hover': r.primaryHover,
    '--e67-secondary': r.secondary,
    '--e67-background': r.background,
    '--e67-surface': r.surface,
    '--e67-on-surface': r.onSurface,
    '--e67-error': r.error,
    '--e67-success': r.success,
    '--e67-on-bg': r.onBackground,
    '--e67-muted-bg': r.mutedOnBackground,
    '--e67-muted': r.muted,
    '--e67-outline': r.outline,
    '--e67-hairline': r.hairline,
    '--e67-soft': r.soft,
    '--e67-elevate': r.elevate,
    '--e67-focus': r.focus,
    '--e67-raised': r.raised,
    '--e67-raised-border': r.raisedBorder,
    '--e67-card-shadow': r.cardShadow,
    '--e67-field': r.field,
    '--e67-field-border': r.fieldBorder,
    '--e67-field-muted': r.fieldMuted,
    '--e67-field-line': r.fieldLine,
    '--e67-field-on': r.fieldOn,
  };
};

/** The custom properties that do not change between light and dark. */
export const shapeVars = (theme: ThemeDoc): Record<string, string> => {
  const radius = radiusScale(theme);
  const scale = lookupNumber(SCALE, theme.typography?.scale ?? 'comfortable', 1);
  const density = lookupNumber(DENSITY, theme.shape?.density ?? 'regular', 1);
  return {
    '--e67-heading-font': `'${safeFont(theme.typography?.headingFamily, 'Fraunces')}'`,
    '--e67-body-font': `'${safeFont(theme.typography?.bodyFamily, 'Inter')}'`,
    '--e67-radius': `${radius.base}px`,
    '--e67-radius-sm': `${radius.sm}px`,
    '--e67-radius-lg': `${radius.lg}px`,
    '--e67-scale': String(scale),
    '--e67-density': String(density),
  };
};

// `:root:root`, not `:root`. styles.css declares the same variables as
// build-time fallbacks so the very first paint is not unstyled, and this sheet
// is installed at runtime — whichever of the two the browser sees last would
// otherwise win. Doubling the pseudo-class makes the event's own palette win on
// specificity instead, which does not depend on load order at all.
const ROOT = ':root:root';

const block = (selector: string, vars: Record<string, string>): string =>
  `${selector}{${Object.entries(vars)
    .map(([k, v]) => `${k}:${v};`)
    .join('')}}`;

/**
 * The whole themed stylesheet as a string: light at the root, dark behind the
 * media query. When the theme carries no dark palette the light one is used for
 * both — an event that never chose dark colours gets its own brand in dark
 * mode, not a palette we invented for it.
 */
export const themeCss = (theme: ThemeDoc): string => {
  const light = { ...paletteVars(theme.light), ...shapeVars(theme) };
  const dark = paletteVars(theme.dark ?? theme.light);
  return [block(ROOT, light), `@media (prefers-color-scheme: dark){${block(ROOT, dark)}}`].join('');
};

/** Whether the event chose its own dark palette, or is wearing its light one. */
export const hasDarkPalette = (theme: ThemeDoc): boolean => Boolean(theme.dark);

/**
 * A theme document from an API response, or the fallback.
 *
 * Never throws: a theme that fails to parse is a cosmetic problem, and blanking
 * an attendee's form over one would turn it into an outage.
 */
export const readTheme = (value: unknown): ThemeDoc => {
  const parsed = ThemeDocSchema.safeParse(value);
  return parsed.success ? parsed.data : FALLBACK_THEME;
};
